import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useRidesMeService, type RidesUploadTypeView } from '@actschurch/ccms-sdk';
import {
  currentUpload,
  docsUsable,
  isLiveUpload,
  levelFor,
  missingIdsFor,
  splitMissing,
  typeNames,
  typesFor,
  useMyDocuments,
  type DocumentContext,
} from '@/lib/documents';
import { documentRules, useCopy } from '@/lib/settings';
import { errorMessage } from '@/lib/copy';
import { Callout } from '@/components/ui';
import { Disclosure } from '@/components/member/Disclosure';
import { DocumentItem } from './DocumentItem';
import { useDocumentViewer } from './DocumentViewer';
import '@/styles/member.css';

const REQUIRED_TEXT: Record<DocumentContext, string> = {
  driver: 'Needed before you can offer a lift',
  rider: 'Needed before you can book a ride',
  all: 'Needed',
};

interface Props {
  context: DocumentContext;
  /** 'all' context: the member also drives, so driver documents are listed too. */
  isDriver?: boolean;
  /** Anchor id (Account uses "documents" so Home's nudge can link to /account#documents). */
  id?: string;
  /** The API just answered 409 documents_required: emphasise what is missing and scroll here. */
  highlight?: boolean;
  /** Section heading; false = no heading (the page renders its own). */
  label?: ReactNode | false;
  /** Called after an upload or delete succeeds. */
  onChanged?: () => void;
  /** No card and no heading (Account puts it inside a settings row). */
  plain?: boolean;
}

export interface DocumentsSummary {
  /** The documents area applies to this member (or failed to load and should say so). */
  visible: boolean;
  /** Short status for a settings row. */
  label: string | null;
  tone: 'ok' | 'warn' | 'info' | 'neutral' | 'bad';
}

/** One-line status of the member's documents for Account (needed / waiting / up to date). */
export function useDocumentsSummary(isDriver?: boolean): DocumentsSummary {
  const { settings } = useCopy();
  const rules = documentRules(settings);
  const docs = useMyDocuments();
  if (!rules.enabled) return { visible: false, label: null, tone: 'neutral' };
  if (docs.isError) return { visible: true, label: null, tone: 'neutral' };
  const view = docs.data;
  if (!docs.isSuccess || !docsUsable(view)) return { visible: false, label: null, tone: 'neutral' };
  const opts = { isDriver, clearanceForDrivers: rules.minorRidesRequireClearedDriver };
  const types = typesFor(view, 'all', opts);
  if (types.length === 0) return { visible: false, label: null, tone: 'neutral' };
  const missing = splitMissing(view, missingIdsFor(view, 'all', isDriver));
  if (missing.toUpload.length > 0) return { visible: true, label: `${missing.toUpload.length} needed`, tone: 'warn' };
  if (missing.awaitingReview.length > 0) return { visible: true, label: 'Waiting for a check', tone: 'info' };
  const added = types.filter((tp) => isLiveUpload(currentUpload(view, tp.uploadTypeID))).length;
  return { visible: true, label: added > 0 ? 'Up to date' : 'Optional', tone: added > 0 ? 'ok' : 'neutral' };
}

/**
 * The member's documents for one context, driven by GET me/documents: only the kinds the settings
 * ask for. Needed documents come first and start open; optional ones sit under "More documents
 * (optional)" when something is needed, so the to-do is clear. Renders nothing while loading,
 * when uploads are off, storage isn't set up, or nothing is asked in this context.
 */
export function DocumentsCard({ context, isDriver, id, highlight, label, onChanged, plain }: Props) {
  const { t, settings } = useCopy();
  const rules = documentRules(settings);
  const docs = useMyDocuments();
  const me = useRidesMeService();
  const viewer = useDocumentViewer((uploadId) => me.getDocumentLink(uploadId));
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (highlight) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [highlight]);

  if (!rules.enabled) return null;
  // Nothing while loading: in most set-ups riders are asked for nothing, so a skeleton would only flash.
  if (docs.isLoading) return null;
  const heading = label === false || plain ? null : label ?? 'Documents';
  const cardClass = plain ? undefined : 'rides-card rides-sectioncard';

  if (docs.error) {
    return (
      <section className={cardClass} id={id} ref={rootRef} style={{ scrollMarginTop: 80 }}>
        {heading && <div className="head"><div style={{ minWidth: 0 }}><h2>{heading}</h2></div></div>}
        <Callout
          tone="bad"
          title="We couldn't load your documents"
          action={<Button variant="default" leftSection={<IconRefresh size={18} />} loading={docs.isRefetching} onClick={() => docs.refetch()}>Try again</Button>}
        >
          {errorMessage(docs.error)}
        </Callout>
      </section>
    );
  }
  const view = docs.data;
  if (!docsUsable(view)) return null;

  const levelOpts = { isDriver, clearanceForDrivers: rules.minorRidesRequireClearedDriver };
  const types = typesFor(view, context, levelOpts);
  if (types.length === 0) return null;

  const isRequired = (tp: RidesUploadTypeView) => levelFor(tp, context, levelOpts) === 'required';
  const required = types.filter(isRequired);
  const optional = types.filter((tp) => !isRequired(tp));
  const missing = splitMissing(view, missingIdsFor(view, context, isDriver));
  const approvalRequired = view.approvalRequired ?? rules.approvalRequired;
  const maxMb = view.maxMb && view.maxMb > 0 ? Math.min(20, view.maxMb) : rules.maxMb;
  const retentionDays = view.retentionDays ?? rules.retentionDays;
  const optionalAdded = optional.filter((tp) => isLiveUpload(currentUpload(view, tp.uploadTypeID))).length;

  const item = (tp: RidesUploadTypeView) => (
    <DocumentItem
      key={tp.uploadTypeID}
      type={tp}
      upload={currentUpload(view, tp.uploadTypeID)}
      level={isRequired(tp) ? 'required' : 'optional'}
      requiredText={REQUIRED_TEXT[context]}
      approvalRequired={approvalRequired}
      maxMb={maxMb}
      highlight={highlight && missing.toUpload.some((m) => m.uploadTypeID === tp.uploadTypeID)}
      onView={(u) => viewer.open(u.rideUploadID, tp.name, u.contentType)}
      viewing={viewer.loadingId !== null && viewer.loadingId === currentUpload(view, tp.uploadTypeID)?.rideUploadID}
      onChanged={onChanged}
    />
  );

  const intro = (
    <>
      {t('docs.intro')}
      {required.length === 0 && ` ${t('documents.all_optional')}`}
    </>
  );

  return (
    <section className={cardClass} id={id} ref={rootRef} style={{ scrollMarginTop: 80 }} aria-label={heading ? undefined : 'Documents'}>
      {heading ? (
        <div className="head">
          <div style={{ minWidth: 0 }}>
            <h2>{heading}</h2>
            <p>{intro}</p>
          </div>
        </div>
      ) : (
        <p className="rm-muted" style={{ marginBottom: 12 }}>{intro}</p>
      )}

      <div className="rm-docs">
        {missing.toUpload.length > 0 && (
          <Callout tone={highlight ? 'bad' : 'warn'} live={highlight}>
            {context === 'all'
              ? t('documents.missing', undefined, { documents: typeNames(missing.toUpload) })
              : t('docs.required', undefined, { documents: typeNames(missing.toUpload) })}
          </Callout>
        )}
        {missing.toUpload.length === 0 && missing.awaitingReview.length > 0 && <Callout tone="info">{t('docs.approval_pending')}</Callout>}

        {required.map(item)}
        {required.length === 0 && optional.map(item)}
      </div>

      {required.length > 0 && optional.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Disclosure
            label="More documents (optional)"
            hint={optionalAdded > 0 ? `${optionalAdded} of ${optional.length} added` : `${optional.length} you can add if you like`}
          >
            <div className="rm-docs" style={{ paddingTop: 4 }}>{optional.map(item)}</div>
          </Disclosure>
        </div>
      )}

      {viewer.error && (
        <div style={{ marginTop: 10 }}>
          <Callout tone="bad" live action={<Button variant="default" onClick={viewer.clearError}>OK</Button>}>{viewer.error}</Callout>
        </div>
      )}

      <hr className="rm-divider" style={{ marginTop: 10 }} />
      <Disclosure label="How we keep your documents safe">
        <p className="rm-small" style={{ color: 'var(--rides-ink)', fontSize: 15 }}>{t('docs.privacy_note', undefined, { retentionDays })}</p>
        <p className="rm-small" style={{ marginTop: 6 }}>{t('documents.resize_note')}</p>
      </Disclosure>
      {viewer.modal}
    </section>
  );
}
